import { Link } from 'react-router-dom'
import { MapPin, Users, Clock, BookOpen } from 'lucide-react'
import { Campaign } from '../../types/database'

interface CampaignCardProps {
  campaign: Campaign
  relatedCourse?: {
    id: string
    title: string
    duration_hours: number | null
  }
}

export default function CampaignCard({ campaign, relatedCourse }: CampaignCardProps) {
  const progress = (campaign.current_signatures / campaign.target_signatures) * 100
  const daysRemaining = campaign.deadline
    ? Math.ceil(
        (new Date(campaign.deadline).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
      )
    : null

  const getUrgencyColor = () => {
    if (campaign.urgency_level === 'urgent') return 'border-urgency bg-urgency-light'
    if (campaign.urgency_level === 'high') return 'border-orange-400 bg-orange-50'
    return 'border-neutral-200'
  }

  const getStatusBadge = () => {
    switch (campaign.status) {
      case 'won':
        return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-semibold">¡Ganada!</span>
      case 'completed':
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-semibold">Completada</span>
      case 'cancelled':
        return <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full font-semibold">Cancelada</span>
      default:
        return null
    }
  }

  return (
    <div className={`bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300 border-2 ${getUrgencyColor()}`}>
      {/* Imagen de la causa */}
      <div className="aspect-video w-full bg-neutral-200 overflow-hidden relative">
        {campaign.image_url ? (
          <img
            src={campaign.image_url}
            alt={campaign.title}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-action-light text-white">
            <span className="text-4xl font-bold">{campaign.title.charAt(0)}</span>
          </div>
        )}
        {campaign.urgency_level === 'urgent' && (
          <div className="absolute top-2 right-2 bg-urgency text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse">
            ¡URGENTE!
          </div>
        )}
      </div>

      {/* Contenido de la tarjeta */}
      <div className="p-4">
        {/* Tema y ubicación */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            {campaign.theme && (
              <span className="px-2 py-1 bg-action-light bg-opacity-10 text-action text-xs rounded-full font-medium">
                {campaign.theme}
              </span>
            )}
            {getStatusBadge()}
          </div>
          {campaign.location && (
            <div className="flex items-center space-x-1 text-xs text-neutral-600">
              <MapPin className="w-3 h-3" />
              <span>{campaign.location}</span>
            </div>
          )}
        </div>

        {/* Título de la campaña */}
        <Link to={`/campanas/${campaign.id}`}>
          <h3 className="font-heading font-semibold text-lg text-neutral-800 mb-3 hover:text-action transition-colors line-clamp-2">
            {campaign.title}
          </h3>
        </Link>

        {/* Barra de progreso de firmas */}
        <div className="mb-2">
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="font-semibold text-neutral-700">
              {campaign.current_signatures.toLocaleString()} / {campaign.target_signatures.toLocaleString()} firmas
            </span>
            <span className="text-xs text-neutral-600">{progress.toFixed(0)}%</span>
          </div>
          <div className="w-full bg-neutral-200 rounded-full h-2 overflow-hidden">
            <div
              className="bg-action h-full transition-all duration-300"
              style={{ width: `${Math.min(progress, 100)}%` }}
            ></div>
          </div>
        </div>

        {/* Tiempo restante */}
        {daysRemaining !== null && campaign.status === 'active' && (
          <div className="flex items-center space-x-1 text-sm text-neutral-600 mb-3">
            <Clock className="w-4 h-4" />
            <span>
              {daysRemaining > 0
                ? `${daysRemaining} ${daysRemaining === 1 ? 'día' : 'días'} restantes`
                : '¡Último día!'}
            </span>
          </div>
        )}

        {/* Organizador */}
        {campaign.organizer_name && (
          <div className="flex items-center space-x-1 text-xs text-neutral-600 mb-3">
            <Users className="w-3 h-3" />
            <span>Organizado por {campaign.organizer_name}</span>
          </div>
        )}

        {/* Botones de acción */}
        <div className="flex space-x-2 mb-3">
          {campaign.status === 'active' ? (
            <>
              <Link to={`/campanas/${campaign.id}`} className="flex-1">
                <button className="w-full bg-action text-white py-2 rounded-lg font-medium hover:bg-action-dark transition-colors">
                  Firmar
                </button>
              </Link>
              <button className="px-4 py-2 border-2 border-urgency text-urgency rounded-lg font-medium hover:bg-urgency hover:text-white transition-colors">
                Compartir
              </button>
            </>
          ) : (
            <Link to={`/campanas/${campaign.id}`} className="flex-1">
              <button className="w-full bg-neutral-300 text-neutral-700 py-2 rounded-lg font-medium hover:bg-neutral-400 transition-colors">
                Ver Detalles
              </button>
            </Link>
          )}
        </div>

        {/* Curso relacionado (mini-card integrado) */}
        {relatedCourse && (
          <div className="p-3 bg-education-light bg-opacity-10 rounded-lg border border-education-light">
            <div className="flex items-start space-x-2">
              <BookOpen className="w-4 h-4 text-education mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-education mb-1">
                  Aprende más sobre este tema
                </p>
                <p className="text-xs text-neutral-700 line-clamp-1">
                  {relatedCourse.title}
                </p>
                {relatedCourse.duration_hours && (
                  <p className="text-xs text-neutral-500 mt-0.5">
                    {relatedCourse.duration_hours}h de contenido
                  </p>
                )}
                <Link
                  to={`/cursos/${relatedCourse.id}`}
                  className="inline-block mt-1"
                >
                  <button className="text-xs bg-education text-white px-3 py-1 rounded hover:bg-education-dark transition-colors">
                    Acceder al Curso
                  </button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
